import { ref, computed, inject } from "vue";
import { useRouter } from "vue-router";
import { useAuthStore } from "@/store/auth.store";
import { useCreditStore } from "@/store/credit.store"; // Store para los créditos
import ToolsService from "@/services/ToolsService";
import Toast from "@/plugins/Toast";
import { ToastType } from "@/types/toast";
import PdfService from "@/services/PdfService";
import { Credit } from "@/models/Credit";

export default class CreditDetailViewModel {
  public toast: Toast;
  public loading = ref<boolean>(false);
  public breadcrumbs = [
    {
      title: "Dashboard",
      disabled: false,
      href: "/app/home",
    },
    {
      title: "Detalle Crédito",
      disabled: true,
      href: "",
    },
  ];
  public scheduleDialogRef: any;
  private authStore = useAuthStore();
  private creditStore = useCreditStore();
  private toolsService = new ToolsService();
  private pdfService = new PdfService();
  private router = useRouter();

  // Computed Properties
  public user = computed(() => this.authStore.getUserData);
  public userName = computed(() => this.user.value?.nom || "Usuario");
  public userEmail = computed(() => this.user.value?.email || "");
  public userInitial = computed(() =>
    this.toolsService.inicial(this.user.value?.nom || "U")
  );

  // Crédito seleccionado desde el store
  public credit = computed(() => this.creditStore.selectedCredit as Credit);
  public credit_vence = computed(() =>
    this.credit.value?.credito_por_vencer?.length > 0
      ? this.credit.value.credito_por_vencer[0]
      : null
  );
  constructor(_scheduleDialogRef: any) {
    this.scheduleDialogRef = _scheduleDialogRef;
    this.toast = inject("toast") as Toast;

    // Validar si hay un crédito seleccionado al inicializar
    if (!this.credit.value) {
      this.toast.showToast(
        "No se encontró un crédito seleccionado.",
        ToastType.Error
      );
      this.router.push("/app/home"); // Redirige al home si no hay crédito
    }
  }
  showSchedule() {
    if (this.credit.value) {
      this.scheduleDialogRef.value?.openDialog(this.credit.value);
    }
  }
  copyToClipboard(credit: Credit): void {
    console.log(credit);

    const text = `Mi ID de socio es: 
          ${this.user.value?.idsocio}
          y mi ID de crédito es: ${credit.idcredito}`;
    navigator.clipboard.writeText(text).then(() => {
      console.log("Datos copiados al portapapeles");
    });
    this.toast.showToast(
      "Información de crédito copiado en el portapapeles. Ctrl + V para pegar el contenido",
      ToastType.Success
    );
  }
  shareCredit(credit: Credit): void {
    console.log(credit);
  }
  /**
   * Format currency based on coin type.
   */
  formatCoin(coin: number): string {
    return this.toolsService.getCoin(coin);
  }

  /**
   * Format float numbers with localization.
   */
  formatFloat(number: number): string {
    return this.toolsService.getFloat(number ?? 0);
  }

  /**
   * Calculate percentage of disbursement.
   */
  percentDisbursement(total: number, remaining: number): number {
    return this.toolsService.percentDisbursement(total, remaining);
  }

  /**
   * Format transaction date.
   */
  formatDate(date: string): string {
    return this.toolsService.formatDate(date);
  }

  /**
   * Get color for the credit state chip.
   */
  getStateColor(daysLate: number): string {
    if (daysLate <= 0) return "green";
    if (daysLate <= 8) return "amber";
    return "red";
  }

  /**
   * Get text for the credit state chip.
   */
  getStateText(daysLate: number): string {
    if (daysLate <= 0) return "Al día";
    if (daysLate <= 8) return "Atrasado";
    return "En mora";
  }

  /**
   * Export the cronograma to PDF.
   */
  async exportToPdf() {
    if (!this.credit.value?.cronogramas?.length) {
      this.toast.showToast(
        "No hay cronogramas disponibles para exportar.",
        ToastType.Info
      );
      return;
    }

    this.loading.value = true;
    try {
      // Aquí implementas la lógica para generar el PDF
      // Ejemplo: llamar un servicio que construya y descargue el PDF
      await this.pdfService.generateCreditPaymentSchedulePdf(
        this.credit.value,
        this.authStore.getUserData
      );

      this.toast.showToast(
        "Cronograma descargado con éxito.",
        ToastType.Success
      );
      this.loading.value = false;
    } catch (error) {
      this.toast.showToast("Error al generar el PDF.", ToastType.Error);
      this.loading.value = false;
    }
  }
  getColor(tipoMov: string): string {
    switch (tipoMov) {
      case "1": // Movimiento de ingreso
        return "success"; // Verde
      case "2": // Movimiento de retiro
        return "error"; // Rojo
      default: // Otro tipo de movimiento
        return "primary"; // Color predeterminado
    }
  }

  getIconName(tipoMov: string): string {
    switch (tipoMov) {
      case "1": // Movimiento de ingreso
        return "mdi-arrow-top-right"; // Flecha hacia arriba
      case "2": // Movimiento de retiro
        return "mdi-arrow-bottom-left"; // Flecha hacia abajo
      default: // Otro tipo de movimiento
        return "mdi-wallet"; // Ícono genérico
    }
  }
}
